import ActionsMenu from "../../../components/ActionsMenu";
import RemoveRouteButton from "./RemoveRouteButton";
import React, { FC, useCallback, useEffect, useState } from "react";
import { useField } from "react-final-form";
import {
  calculateDurationInSeconds,
  calculateDurationInString,
  colors,
  generateRouteName,
  getSelectedRoutePlanVersion,
} from "../tools";
import SanitizedGrid from "../../../components/SanitizedGrid";
import { Chip, Grid, Typography } from "@material-ui/core";
import ResetOptimisedRouteButton from "./ResetOptimisedRouteButton";
import CustomMuiDataGrid from "../../../components/CustomMuiDataGrid";
import DetailRouteButton from "./DetailRouteButton";
import { find, groupBy, map, truncate } from "lodash";
import MoveUnAssignedLocationButton from "./MoveUnAssignedLocationButton";
import AddRouteButton from "./AddRouteButton";
import Stack from "../../../components/Stack";
import RemoveUnAssignedLocationButton from "./RemoveUnAssignedLocationButton";
import RemoveSelectedUnAssignedLocationButton from "./RemoveSelectedUnAssignedLocationButton";
import { useDataProvider } from "react-admin";
import UnassignedLocationsWarningAlert from "./UnassignedLocationsWarningAlert";
import { useDispatch } from "react-redux";
import { changeIsFinishFetchOptimisationRoute } from "../../../redux/navmode.action";
import RouteOptimisationConstraintAlert from "./RouteOptimisationConstraintAlert";
import AiLoading from "./AiLoading";
import ReadjustAllRouteButton from "./ReadjustAllRouteButton";
import RouteVersionButton from "./RouteVersionButton";

const groupStudentsByCoordinates = (students: any[]) => {
  // Group students by lat/lng (rounded to avoid precision issues)
  const grouped = groupBy(students, (student) => {
    const address = student.address;
    if (!address || address.lat == null || address.lng == null)
      return "unknown";

    const precision = 6;
    const lat = parseFloat(address.lat.toFixed(precision));
    const lng = parseFloat(address.lng.toFixed(precision));
    return `${lat},${lng}`;
  });

  // Convert grouped object into desired array format
  return map(grouped, (studentsInGroup) => {
    const exampleStudent = studentsInGroup[0];
    const address = exampleStudent.address;

    return {
      lat: address.lat,
      lng: address.lng,
      street: address.street,
      unitNumber: address.unitNumber,
      blockNumber: address.blockNumber,
      postalCode: address.postalCode,
      building: address.building,
      address: `${address.blockNumber ?? ""} ${address.street ?? ""} ${
        address.building ?? ""
      } ${address.postalCode}`,
      studentIds: studentsInGroup.map((s) => s.id),
    };
  });
};

interface AllOptimiseRouteComponentProps {
  setIsOpenDetailRoute: any;
  mode: "create" | "update";
  progressMap: number;
}

const AllOptimiseRouteComponent: FC<AllOptimiseRouteComponentProps> = ({
  setIsOpenDetailRoute,
  mode,
  progressMap,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [unassignedLocationSelectedIds, setUnassignedLocationSelectedIds] =
    useState([]);

  const routesField = useField("routes");
  const droppedLocationsField = useField("droppedLocations");
  const schoolField = useField("school");
  const busScheduleField = useField("busSchedule");
  const studentsField = useField("students");
  const maxVehicleCapacityField = useField("maxVehicleCapacity");
  const desiredRouteDurationField = useField("desiredRouteDuration");
  const maxStopTimeField = useField("maxStopTime");

  const formStudentsChangedField = useField("formStudentsChanged");
  const formRoutesChangedField = useField("formRoutesChanged");
  const formOptimizationConstraintsChangedField = useField(
    "formOptimizationConstraintsChanged",
  );

  const provider = useDataProvider();
  const dispatch = useDispatch();

  const selectedVersion = getSelectedRoutePlanVersion();

  const routeDataColumns = [
    {
      field: "routeName",
      headerName: "Route Name",
      width: 160,
      renderCell: ({ row }: any) => {
        if (
          mode === "create" &&
          calculateDurationInSeconds(row.startTime, row.endTime) >
            desiredRouteDurationField.input.value * 60
        ) {
          return (
            <Stack direction="row" style={{ width: "100%" }}>
              <span>{row.routeName}</span>
              <span
                style={{
                  fontSize: "12px",
                  backgroundColor: "#ff9800",
                  borderRadius: "14px",
                  color: "white",
                  padding: "2px 4px",
                }}
              >
                overtime
              </span>
            </Stack>
          );
        }

        return row.routeName;
      },
    },
    {
      field: "totalStop",
      headerName: "Total Stops",
      width: 160,
      sortable: false,
      valueGetter: ({ row }: any) => row.routeLocations.length - 1, // because the one is the school location
    },
    {
      field: "totalStudents",
      headerName: "Total Students",
      width: 160,
      sortable: false,
      valueGetter: ({ row }: any) =>
        row.routeLocations.reduce(
          (total: number, location: any) => total + location.students.length,
          0,
        ),
    },
    {
      field: "durations",
      headerName: "Durations",
      width: 160,
      sortable: false,
      valueGetter: ({ row }: any) =>
        row.startTime && row.endTime
          ? calculateDurationInString(row.startTime, row.endTime)
          : "loading...",
    },
    {
      field: "routeColor",
      headerName: "Legend",
      width: 120,
      sortable: false,
      renderCell: ({ row }: any) => {
        return (
          <div
            style={{
              backgroundColor: row.routeColor.colorTwo,
              width: "20px",
              height: "20px",
              borderRadius: 5,
            }}
          />
        );
      },
    },
    {
      field: "actions",
      headerName: "Actions",
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      disableReorder: true,
      width: 320,
      renderCell: ({ row }: any) => {
        return (
          <ActionsMenu
            render={[
              <DetailRouteButton
                routeName={row.routeName}
                setIsOpenDetailRoute={setIsOpenDetailRoute}
              />,
              <RemoveRouteButton routeName={row.routeName} mode={mode} />,
            ]}
          />
        );
      },
    },
  ];

  const droppedLocationDataColumns = [
    {
      field: "no",
      headerName: "No",
      width: 95,
      valueGetter: (props: any) => {
        return props.api.getRowIndex(props.row.id) + 1;
      },
    },
    {
      field: "address",
      headerName: "Full Address",
      width: 320,
    },
    {
      field: "students",
      headerName: "Students",
      width: 320,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      disableReorder: true,
      renderCell: ({ row }: any) => {
        return (
          <Stack
            direction="row"
            spacing={1}
            justifyContent="flex-start"
            style={{ padding: 8 }}
          >
            {row.students.map((student: any, index: number) => (
              <Chip
                key={`student-key-${index}`}
                label={`${truncate(`${student.firstName} ${student.lastName}`, {
                  length: 15,
                })}`}
              />
            ))}
          </Stack>
        );
      },
    },
    {
      field: "routeColor",
      headerName: "Legend",
      width: 110,
      sortable: false,
      renderCell: () => {
        return (
          <div
            style={{
              backgroundColor: "#3F3F46",
              width: "20px",
              height: "20px",
              borderRadius: 5,
            }}
          />
        );
      },
    },
    {
      field: "actions",
      headerName: "Actions",
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      disableReorder: true,
      width: 320,
      renderCell: ({ row }: any) => {
        return (
          <ActionsMenu
            render={[
              <MoveUnAssignedLocationButton routeLocationId={row.id} />,
              <RemoveUnAssignedLocationButton routeLocationId={row.id} />,
            ]}
          />
        );
      },
    },
  ];

  const handleResetSelectedValues = () => {
    setUnassignedLocationSelectedIds([]);
  };

  const handleFetchOptimisedRoute = useCallback(async () => {
    setLoading(true);
    setError(null);
    dispatch(changeIsFinishFetchOptimisationRoute(false));

    try {
      let resultRoutes;
      let droppedLocations;

      const { data: academicYears } = await provider.customGetList(
        "academicYears",
        {
          filter: {
            SchoolId: schoolField.input.value.id,
            StatusIn: ["Ready", "Active"],
          },
        },
      );

      // prefilled assigned subcontractor dates based on discussion with Vena
      const readyAcademicYear = academicYears.find(
        (academicYear: any) => academicYear.status === "ready",
      );
      const activeAcademicYear = academicYears.find(
        (academicYear: any) => academicYear.status === "active",
      );

      let prefilledAssignedSubcontractor = {
        startDate: "",
        endDate: "",
      };

      if (readyAcademicYear) {
        prefilledAssignedSubcontractor.startDate = readyAcademicYear.startDate;
        prefilledAssignedSubcontractor.endDate = readyAcademicYear.endDate;
      } else if (activeAcademicYear) {
        prefilledAssignedSubcontractor.startDate = activeAcademicYear.startDate;
        prefilledAssignedSubcontractor.endDate = activeAcademicYear.endDate;
      } else {
        prefilledAssignedSubcontractor.startDate = "";
        prefilledAssignedSubcontractor.endDate = "";
      }

      if (busScheduleField.input.value.type === "to_school") {
        const requestJson = {
          destinationLocation: {
            lat: schoolField.input.value.lat,
            lng: schoolField.input.value.lng,
            street: schoolField.input.value.street,
            unitNumber: schoolField.input.value.unitNumber,
            blockNumber: schoolField.input.value.blockNumber,
            building: schoolField.input.value.building,
            address: schoolField.input.value.address,
            postalCode: schoolField.input.value.postalCode,
          },
          desiredArrivalTime: busScheduleField.input.value.arrivalTime
            .split(":")
            .slice(0, 2)
            .join(":"),
          maxVehicleCapacity: Number(maxVehicleCapacityField.input.value),
          desiredRouteDuration: desiredRouteDurationField.input.value * 60,
          maxStopTime: maxStopTimeField.input.value * 60,
          locations: groupStudentsByCoordinates(studentsField.input.value),
        };

        const responseToSchool = await provider.customCreate(
          "routeOptimization/pickUp",
          {
            data: requestJson,
          },
        );

        resultRoutes = JSON.parse(JSON.stringify(responseToSchool.data.routes));
        droppedLocations = JSON.parse(
          JSON.stringify(responseToSchool.data.droppedLocations),
        );
      } else if (
        busScheduleField.input.value.type === "from_school" ||
        busScheduleField.input.value.type === "cca"
      ) {
        const requestJson = {
          originLocation: {
            lat: schoolField.input.value.lat,
            lng: schoolField.input.value.lng,
            street: schoolField.input.value.street,
            unitNumber: schoolField.input.value.unitNumber,
            blockNumber: schoolField.input.value.blockNumber,
            building: schoolField.input.value.building,
            address: schoolField.input.value.address,
            postalCode: schoolField.input.value.postalCode,
          },
          desiredDepartureTime: busScheduleField.input.value.arrivalTime
            .split(":")
            .slice(0, 2)
            .join(":"),
          maxVehicleCapacity: Number(maxVehicleCapacityField.input.value),
          desiredRouteDuration: desiredRouteDurationField.input.value * 60,
          maxStopTime: maxStopTimeField.input.value * 60,
          locations: groupStudentsByCoordinates(studentsField.input.value),
        };

        const responseFromSchool = await provider.customCreate(
          "routeOptimization/dropOff",
          {
            data: requestJson,
          },
        );

        resultRoutes = JSON.parse(
          JSON.stringify(responseFromSchool.data.routes),
        );
        droppedLocations = JSON.parse(
          JSON.stringify(responseFromSchool.data.droppedLocations),
        );
      }

      const transformedRoutes = resultRoutes.map(
        (route: any, index: number) => {
          const routeName = generateRouteName(
            busScheduleField.input.value.type,
            index + 1,
          );

          return {
            id: index + 1,
            realRouteId: 0,
            routeName: routeName,
            startTime: "",
            endTime: "",
            busBayNumber: null,
            driverId: null,
            busAssistantId: null,
            vehicleId: null,
            backupDriverIds: [],
            backupBusAssistantIds: [],
            backupVehicleIds: [],
            assignedSubcontractor: prefilledAssignedSubcontractor,
            routeColor: colors[index % colors.length], // if colors are not enough, roll over the colors array to first index
            routeGeometries: [],
            routeLocations: route.locations.map(
              (location: any, index: number) => {
                let locationCodeIndex;

                if (busScheduleField.input.value.type === "to_school") {
                  locationCodeIndex = index;
                } else {
                  locationCodeIndex = index - 1;
                }

                // convert ASCII Code into Alphabet
                // 65 === A
                const locationCode = String.fromCharCode(
                  65 + locationCodeIndex,
                );

                return {
                  id: 0,
                  lat: location.lat,
                  lng: location.lng,
                  street: location.street,
                  unitNumber: location.unitNumber,
                  blockNumber: location.blockNumber,
                  building: location.building,
                  address: location.address,
                  postalCode: location.postalCode,
                  order:
                    busScheduleField.input.value.type === "to_school"
                      ? index + 1
                      : index,
                  arrivalTime: "",
                  remark: null,
                  students: location.studentIds
                    ? location.studentIds.map(
                        (studentId: any, index: number) => {
                          const findStudent = find(studentsField.input.value, [
                            "id",
                            studentId,
                          ]);

                          return {
                            ...findStudent,
                            locationCode: `${locationCode}${index + 1}`,
                          };
                        },
                      )
                    : [],
                } as any;
              },
            ),
          } as any;
        },
      );

      const transformedDroppedLocations = droppedLocations.map(
        (location: any, index: number) => ({
          id: index + 1,
          lat: location.lat,
          lng: location.lng,
          street: location.street,
          unitNumber: location.unitNumber,
          blockNumber: location.blockNumber,
          building: location.building,
          address: location.address,
          postalCode: location.postalCode,
          order: "",
          arrivalTime: "",
          students: location.studentIds
            ? location.studentIds.map((studentId: any) => {
                const findStudent = find(studentsField.input.value, [
                  "id",
                  studentId,
                ]);

                return {
                  ...findStudent,
                  locationCode: "",
                };
              })
            : [],
        }),
      );

      formStudentsChangedField.input.onChange(false);
      formOptimizationConstraintsChangedField.input.onChange(false);

      routesField.input.onChange(transformedRoutes);
      droppedLocationsField.input.onChange(transformedDroppedLocations);
    } catch (err) {
      setError(err.message || "An unexpected error occurred.");
    } finally {
      setLoading(false);
      dispatch(changeIsFinishFetchOptimisationRoute(true));
    }
  }, []);

  useEffect(() => {
    if (mode === "create" && routesField.input.value.length === 0) {
      handleFetchOptimisedRoute();
    }
  }, []);

  useEffect(() => {
    if (
      (formStudentsChangedField.input.value === true ||
        formOptimizationConstraintsChangedField.input.value === true) &&
      routesField.input.value.length > 0
    ) {
      handleFetchOptimisedRoute();
    }
  }, [
    formStudentsChangedField.input.value,
    formOptimizationConstraintsChangedField.input.value,
  ]);

  return (
    <SanitizedGrid container spacing={2}>
      {mode === "create" && formRoutesChangedField.input.value && (
        <Grid item xs={12}>
          <ResetOptimisedRouteButton
            loading={loading}
            handleFetchOptimisedRoute={handleFetchOptimisedRoute}
          />
        </Grid>
      )}

      {mode === "update" && (
        <Grid item xs={12}>
          <Stack direction="row" spacing={2} justifyContent="space-between">
            <ReadjustAllRouteButton />
            <RouteVersionButton />
          </Stack>
        </Grid>
      )}

      {loading && (
        <Grid item xs={12} style={{ textAlign: "center", marginTop: "20px" }}>
          <AiLoading isLoading={loading} />
        </Grid>
      )}

      {error && (
        <Grid item xs={12}>
          <Typography color="error" variant="body1">
            {error}
          </Typography>
        </Grid>
      )}

      {!loading && (
        <Grid item xs={12}>
          {droppedLocationsField.input.value.length > 0 ? (
            <UnassignedLocationsWarningAlert />
          ) : mode === "create" ? (
            <RouteOptimisationConstraintAlert />
          ) : (
            <></>
          )}
        </Grid>
      )}

      {droppedLocationsField.input.value.length > 0 && !loading && (
        <>
          <Grid item xs={12} style={{ height: 400 }}>
            <Typography variant="h6" gutterBottom>
              Unassigned Locations
            </Typography>

            <CustomMuiDataGrid
              key={droppedLocationsField.input.value.length}
              columnsToSearch={["address"]}
              rows={droppedLocationsField.input.value}
              columns={droppedLocationDataColumns}
              checkboxSelection
              disableSelectionOnClick
              onSelectionModelChange={(newSelectionModel) => {
                setUnassignedLocationSelectedIds(newSelectionModel);
              }}
              sortModel={[
                {
                  field: "no",
                  sort: "asc",
                },
              ]}
              actions={
                unassignedLocationSelectedIds.length > 0 && (
                  <RemoveSelectedUnAssignedLocationButton
                    routeLocationIds={unassignedLocationSelectedIds}
                    handleResetSelectedValues={handleResetSelectedValues}
                  />
                )
              }
            />
          </Grid>
        </>
      )}

      {!loading && (
        <Grid
          item
          xs={12}
          style={{
            height: 500,
          }}
        >
          <Stack direction="row" spacing={2} justifyContent="flex-start">
            <Typography variant="h6" gutterBottom>
              Routes
            </Typography>

            {mode === "update" && (
              <Chip
                label={`Version: ${selectedVersion ? selectedVersion.id : "-"}`}
                color="primary"
                size="small"
              />
            )}
          </Stack>

          <CustomMuiDataGrid
            key={routesField.input.value.length + progressMap}
            actions={
              <AddRouteButton setIsOpenDetailRoute={setIsOpenDetailRoute} />
            }
            sortModel={[
              {
                field: "routeName",
                sort: "asc",
              },
            ]}
            columnsToSearch={["routeName"]}
            rows={routesField.input.value}
            columns={routeDataColumns}
          />
        </Grid>
      )}
    </SanitizedGrid>
  );
};

export default AllOptimiseRouteComponent;
